/* eslint-disable react/prop-types */
import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { env } from '../config';
import {
  createFrameForProfile,
  destroyFrame,
  frameHide,
  frameNavigate,
  frameShow,
  queueMessage,
} from '../views/SierraView/host';
import { useProfile, useRedirectToPearson } from 'hooks';

type SierraApi = {
  navigateToSierraRoute: (sierraRoute: string) => void;
  getFrameId: () => string;
  isEnabled: () => boolean;
  hideSierraView: () => void;
  getCurrentRouteTitle: () => string;
  registerCommandCallback: (cb: (cmd: string) => void) => void;
  unRegisterCommandCallback: (cb: (cmd: string) => void) => void;
  sendCustomCommand: (command: string, payload: any) => void;
};

const certIframeId = 'certification';
let sierraInitialized = false;
let commandCallbacks = [] as Array<(cmd: any) => void>;
const SierraContext = createContext<SierraApi | undefined>(undefined);

export const useSierra = () => {
  const ctx = useContext(SierraContext);
  if (!ctx) throw new Error('useSierra must be used within SierraProvider');
  return ctx;
};

export const SierraProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const redirectToPearson = useRedirectToPearson();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, { error: profileError, isLoading: isLoadingProfile }] =
    useProfile();
  const [currentRoute, setCurrentRoute] = useState('');

  useEffect(() => {
    const match = location.pathname.match(/^\/certification\/([^&?]+)/);
    if (match && env.USE_SIERRA && !sierraInitialized && profile != null) {
      initSierra(match[1]);
      frameShow(certIframeId);
    }
  }, [profile, location]);

  const navigateToSierraRoute = (sierraRoute: string) => {
    if (env.USE_SIERRA) {
      if (profile?.compTIAId == null) {
        switch (sierraRoute) {
          case 'exams':
            redirectToPearson();
            break;
          default:
            window.open('https://www.comptia.org/certifications', '_blank');
            break;
        }
      } else {
        initSierra(sierraRoute);
        if (!location.pathname.toLowerCase().startsWith(`/${certIframeId}/`))
          navigate(`/${certIframeId}/${sierraRoute}`);
        frameNavigate(certIframeId, sierraRoute);
        if (currentRoute !== sierraRoute) {
          setTimeout(() => setCurrentRoute(sierraRoute), 1);
        }
      }
    }
  };

  const sendCustomCommand = (command: string, payload: any) => {
    queueMessage(
      certIframeId,
      JSON.stringify({
        command: 'CUSTOM_COMMAND',
        payload: { command, payload },
      }),
    );
  };

  const getFrameId = () => {
    return certIframeId;
  };

  const isEnabled = () => {
    return env.USE_SIERRA;
  };

  const hideSierraView = () => {
    frameHide(certIframeId);
  };

  const getCurrentRouteTitle = () => {
    switch (currentRoute) {
      case 'exams':
        return 'Exams';
      case 'certifications':
        return 'Certifications';
      case 'continuingeducation':
        return 'Continuing Education';
      default:
        return 'Certification';
    }
  };

  const registerCommandCallback = (cb: (cmd: any) => void) => {
    commandCallbacks.push(cb);
  };

  const unRegisterCommandCallback = (cb: (cmd: any) => void) => {
    commandCallbacks = commandCallbacks.filter((fn) => fn !== cb);
  };

  const initSierra = (initialRoute: string) => {
    if (sierraInitialized) return;
    sierraInitialized = true;

    setCurrentRoute(initialRoute);
    if (env.USE_SIERRA) {
      const certSiteURL = env.SIERRA_URL;
      let frameSrc = certSiteURL + `?host_frameid=${certIframeId}`;
      if (initialRoute != null && initialRoute.length > 0)
        frameSrc +=
          '&hideNavBar=true&initialRoute=' + encodeURIComponent(initialRoute);

      if (
        profile &&
        profile.iframeWebToken != null &&
        profile.iframeWebToken.length > 0
      ) {
        createFrameForProfile(profile, certIframeId, frameSrc, (cmd) => {
          switch (cmd.command) {
            case 'MANAGE_EXAMS':
              setTimeout(() => {
                frameHide(certIframeId);
                navigate('/profile/pearson', { replace: true });
              }, 1);
              break;
          }
          for (let i = 0; i < commandCallbacks.length; i++) {
            try {
              commandCallbacks[i](cmd);
            } catch {}
          }
        });
      } else destroyFrame(certIframeId);
    }
  };

  return (
    <SierraContext.Provider
      value={{
        navigateToSierraRoute,
        getFrameId,
        isEnabled,
        hideSierraView,
        getCurrentRouteTitle,
        registerCommandCallback,
        unRegisterCommandCallback,
        sendCustomCommand,
      }}
    >
      {children}
    </SierraContext.Provider>
  );
};
